/**
 * SideEffectTags — tappable "pill tag" list of a medication's side effects.
 *
 * The left side opens a symptom detail modal. The right-side + logs that
 * symptom for today via the same symptom tracker that feeds the calendar.
 */
import { useEffect, useMemo, useState } from "react";
import { getTodayString } from "@src/packages/utils/date";
import { ScrollView, Text, View } from "react-native";
import { Check, Plus } from "lucide-react-native";
import {
  colors,
  Dialog,
  DialogHeader,
  IconButton,
  MoonFilterChip,
  PrimaryButton,
  SecondaryButton,
  TextField,
} from "@louise/moon-ui";
import { EMBER } from "../../onboarding/theme";
import { useLocale, useTranslations } from "@src/packages/i18n";
import {
  useSymptomLogs,
  useLogSymptom,
  useDeleteSymptomLog,
} from "@src/packages/care/hooks/useSymptomLogs";
import type { Symptom } from "@src/packages/care/hooks/useSymptomLogs";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

type SymptomTone = "watch" | "call" | "urgent";

type SymptomDetail = {
  icon: string;
  tone: SymptomTone;
  severity: string;
  normal: string;
  dangerous: string;
  callDoctor: string;
};

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

function hasAny(value: string, terms: string[]): boolean {
  return terms.some((term) => value.includes(term));
}

function getSymptomIcon(effect: string): string {
  const s = normalize(effect);
  if (
    hasAny(s, [
      "period",
      "menstrual",
      "bleeding",
      "saignement",
      "règle",
      "règles",
    ])
  ) {
    return "🩸";
  }
  if (hasAny(s, ["ovulation"])) return "🥚";
  if (
    hasAny(s, [
      "hirsutism",
      "hirsutisme",
      "hair growth",
      "facial hair",
      "body hair",
      "pilosité",
    ])
  ) {
    return "〰️";
  }
  if (
    hasAny(s, [
      "thinning hair",
      "hair loss",
      "scalp hair",
      "cheveux",
      "cuir chevelu",
    ])
  ) {
    return "💇";
  }
  if (hasAny(s, ["acne", "oily skin", "peau grasse"])) return "🧴";
  if (
    hasAny(s, [
      "acanthosis",
      "darkening",
      "darkened skin",
      "assombr",
      "plaques de peau",
    ])
  ) {
    return "◐";
  }
  if (
    hasAny(s, ["ohss", "sho", "hyperstimulation", "hyperstimulation ovarienne"])
  ) {
    return "⚠️";
  }
  if (
    hasAny(s, [
      "allergic",
      "allergique",
      "hypersensitivity",
      "hypersensibilité",
    ])
  ) {
    return "🚨";
  }
  if (
    hasAny(s, [
      "blood clot",
      "caillot",
      "clot",
      "thrombop",
      "bruis",
      "ecchymose",
    ])
  ) {
    return "🩸";
  }
  if (
    hasAny(s, [
      "fever",
      "fièvre",
      "chills",
      "frissons",
      "flu-like",
      "pseudo-grippal",
    ])
  ) {
    return "🌡️";
  }
  if (
    hasAny(s, [
      "shortness of breath",
      "essoufflement",
      "palpitations",
      "racing heart",
      "tachycardie",
      "heart rhythm",
    ])
  ) {
    return "🫀";
  }
  if (
    hasAny(s, ["vision", "visual", "blurred", "retinal", "rétine", "visuels"])
  ) {
    return "👁️";
  }
  if (
    hasAny(s, [
      "headache",
      "migraine",
      "mal de tête",
      "maux de tête",
      "meningitis",
      "méningite",
    ])
  ) {
    return "🤕";
  }
  if (
    hasAny(s, [
      "nausea",
      "nausée",
      "vomit",
      "diarrhea",
      "diarrhée",
      "constipation",
      "stomach",
      "gastric",
      "digest",
      "heartburn",
      "acidose",
      "abdominal",
    ])
  ) {
    return "🤢";
  }
  if (
    hasAny(s, [
      "bloat",
      "ballonnement",
      "fluid retention",
      "water retention",
      "rétention",
      "swelling",
      "gonflement",
      "pelvic",
      "cramp",
      "crampe",
    ])
  ) {
    return "🎈";
  }
  if (
    hasAny(s, ["injection", "point d'injection", "patch site", "site du patch"])
  ) {
    return "💉";
  }
  if (
    hasAny(s, [
      "dizziness",
      "vertige",
      "lightheaded",
      "étourdissement",
      "drowsiness",
      "somnolence",
      "sedation",
      "coordination",
    ])
  ) {
    return "😵‍💫";
  }
  if (hasAny(s, ["fatigue", "tired", "weakness", "faiblesse"])) return "🪫";
  if (
    hasAny(s, [
      "mood",
      "humeur",
      "irritab",
      "anxiety",
      "anxiété",
      "depression",
      "dépression",
      "insomnia",
      "insomnie",
      "sleep",
    ])
  ) {
    return "🌙";
  }
  if (
    hasAny(s, [
      "hot flash",
      "hot flush",
      "bouffée",
      "flushing",
      "warmth",
      "sweat",
      "sueur",
    ])
  ) {
    return "🔥";
  }
  if (hasAny(s, ["breast", "sein", "mammaire"])) return "🎀";
  if (
    hasAny(s, [
      "vaginal",
      "uterine",
      "utérus",
      "cervical",
      "endomètre",
      "yeast",
      "mycose",
    ])
  ) {
    return "🪷";
  }
  if (
    hasAny(s, [
      "skin",
      "rash",
      "itch",
      "acne",
      "photosensitivity",
      "peau",
      "éruption",
      "démangeaison",
      "soleil",
    ])
  ) {
    return "✨";
  }
  if (
    hasAny(s, [
      "joint",
      "muscle",
      "bone",
      "back",
      "ache",
      "douleur",
      "articulaire",
      "musculaire",
      "osseuse",
      "dos",
    ])
  ) {
    return "🦴";
  }
  if (
    hasAny(s, [
      "appetite",
      "weight",
      "blood sugar",
      "glycémie",
      "vitamin b12",
      "foie",
      "liver",
    ])
  ) {
    return "⚖️";
  }
  return "🩺";
}

function detailText(effect: string, locale: "en" | "fr"): SymptomDetail {
  const s = normalize(effect);
  const fr = locale === "fr";
  const icon = getSymptomIcon(effect);

  if (
    hasAny(s, ["ohss", "sho", "hyperstimulation", "hyperstimulation ovarienne"])
  ) {
    return {
      icon,
      tone: "urgent",
      severity: fr
        ? "Potentiellement sérieux. Les symptômes de SHO peuvent s'aggraver rapidement, surtout après le déclenchement ou la ponction."
        : "Potentially serious. OHSS symptoms can worsen quickly, especially after a trigger shot or egg retrieval.",
      normal: fr
        ? "Un ballonnement léger et une gêne pelvienne peuvent arriver pendant la stimulation."
        : "Mild bloating and pelvic heaviness can happen during stimulation.",
      dangerous: fr
        ? "Douleur importante, ventre qui gonfle vite, vomissements, essoufflement, malaise, prise de poids rapide ou urines très diminuées."
        : "Severe pain, fast abdominal swelling, vomiting, shortness of breath, faintness, rapid weight gain, or much less urine.",
      callDoctor: fr
        ? "Appelez votre clinique le jour même. Si l'essoufflement, le malaise ou la douleur est sévère, utilisez les urgences."
        : "Call your clinic the same day. For shortness of breath, fainting, or severe pain, use urgent care.",
    };
  }

  if (
    hasAny(s, [
      "allergic",
      "allergique",
      "hypersensitivity",
      "hypersensibilité",
    ])
  ) {
    return {
      icon,
      tone: "urgent",
      severity: fr
        ? "Rare, mais potentiellement sérieux si la réaction s'étend au-delà d'une irritation locale."
        : "Rare, but potentially serious if it goes beyond mild local irritation.",
      normal: fr
        ? "Une petite rougeur ou démangeaison au point d'injection peut être courante."
        : "A small area of redness or itching at the injection site can be common.",
      dangerous: fr
        ? "Urticaire généralisée, gonflement du visage ou des lèvres, respiration difficile, oppression ou malaise."
        : "Widespread hives, swelling of the face or lips, trouble breathing, chest tightness, or faintness.",
      callDoctor: fr
        ? "Appelez immédiatement en cas de réaction généralisée. Utilisez les urgences si la respiration ou le visage sont touchés."
        : "Call immediately for a body-wide reaction. Use emergency care if breathing or facial swelling is involved.",
    };
  }

  if (
    hasAny(s, [
      "blood clot",
      "caillot",
      "clot",
      "shortness of breath",
      "essoufflement",
      "palpitations",
      "heart rhythm",
      "retinal",
      "vision",
      "visual",
      "blurred",
      "visuels",
      "meningitis",
      "méningite",
    ])
  ) {
    return {
      icon,
      tone: "call",
      severity: fr
        ? "À surveiller de près. Ce symptôme peut être bénin, mais certains signes associés nécessitent un avis rapide."
        : "Needs close attention. This can be minor, but certain patterns need prompt medical advice.",
      normal: fr
        ? "Des symptômes légers et courts peuvent arriver avec les changements hormonaux ou certains traitements."
        : "Mild, short-lived symptoms can happen with hormone shifts or certain medications.",
      dangerous: fr
        ? "Symptôme soudain, sévère, inhabituel, qui empire, ou associé à essoufflement, douleur thoracique, faiblesse d'un côté, troubles visuels ou confusion."
        : "Sudden, severe, unusual, worsening, or paired with shortness of breath, chest pain, one-sided weakness, vision changes, or confusion.",
      callDoctor: fr
        ? "Contactez votre clinique rapidement. Utilisez les urgences pour douleur thoracique, essoufflement, faiblesse neurologique ou troubles visuels sévères."
        : "Contact your clinic promptly. Use urgent care for chest pain, shortness of breath, neurologic weakness, or severe vision changes.",
    };
  }

  if (
    hasAny(s, [
      "injection",
      "point d'injection",
      "patch site",
      "site du patch",
      "bruis",
      "ecchymose",
    ])
  ) {
    return {
      icon,
      tone: "watch",
      severity: fr
        ? "Souvent léger à modéré. La douleur, un bleu ou une petite induration peuvent être fréquents avec les injections."
        : "Often mild to moderate. Soreness, bruising, or a small firm area can be common with injections.",
      normal: fr
        ? "Sensible pendant un jour ou deux, petit bleu, rougeur limitée ou petite bosse qui diminue."
        : "Tender for a day or two, a small bruise, limited redness, or a small lump that settles.",
      dangerous: fr
        ? "Rougeur qui s'étend, chaleur importante, pus, fièvre, douleur qui empire, grosse bosse persistante ou réaction allergique."
        : "Spreading redness, warmth, pus, fever, worsening pain, a large persistent lump, or allergic symptoms.",
      callDoctor: fr
        ? "Appelez si le site empire, semble infecté, ou si vous ne pouvez pas injecter correctement."
        : "Call if the site is getting worse, looks infected, or you cannot inject correctly.",
    };
  }

  if (
    hasAny(s, [
      "nausea",
      "nausée",
      "vomit",
      "diarrhea",
      "diarrhée",
      "constipation",
      "stomach",
      "gastric",
      "digest",
      "heartburn",
      "abdominal",
      "bloat",
      "ballonnement",
      "cramp",
      "crampe",
      "pelvic",
    ])
  ) {
    return {
      icon,
      tone: "watch",
      severity: fr
        ? "Généralement léger à modéré. Notez l'intensité, la durée, et ce qui l'aggrave ou l'améliore."
        : "Usually mild to moderate. Track the intensity, duration, and what makes it better or worse.",
      normal: fr
        ? "Une gêne légère, des nausées, ballonnements ou crampes peuvent arriver avec les médicaments de fertilité."
        : "Mild discomfort, nausea, bloating, or cramping can happen with fertility medications.",
      dangerous: fr
        ? "Douleur forte, vomissements persistants, ventre qui gonfle vite, incapacité à boire, fièvre, sang dans les selles ou symptômes de SHO."
        : "Severe pain, persistent vomiting, fast abdominal swelling, inability to keep fluids down, fever, blood in stool, or OHSS symptoms.",
      callDoctor: fr
        ? "Appelez si c'est sévère, nouveau, s'aggrave, ou vous empêche de manger, boire, uriner ou marcher normalement."
        : "Call if it is severe, new, worsening, or keeps you from eating, drinking, urinating, or walking normally.",
    };
  }

  if (
    hasAny(s, [
      "fever",
      "fièvre",
      "chills",
      "frissons",
      "flu-like",
      "pseudo-grippal",
    ])
  ) {
    return {
      icon,
      tone: "call",
      severity: fr
        ? "Une sensation pseudo-grippale légère peut arriver avec certains traitements, mais la fièvre doit être surveillée."
        : "A mild flu-like feeling can happen with some treatments, but fever should be watched.",
      normal: fr
        ? "Frissons légers ou courbatures peu après certains médicaments peuvent être attendus si votre clinique vous l'a mentionné."
        : "Mild chills or aches after certain medications may be expected if your clinic warned you.",
      dangerous: fr
        ? "Température de 38 °C / 100,4 °F ou plus, fièvre persistante, frissons intenses, douleur importante ou signe d'infection."
        : "Temperature of 38°C / 100.4°F or higher, persistent fever, shaking chills, severe pain, or signs of infection.",
      callDoctor: fr
        ? "Appelez votre clinique le jour même pour une vraie fièvre ou si vous vous sentez franchement malade."
        : "Call your clinic the same day for a true fever or if you feel clearly unwell.",
    };
  }

  if (
    hasAny(s, [
      "dizziness",
      "vertige",
      "lightheaded",
      "étourdissement",
      "drowsiness",
      "somnolence",
      "sedation",
      "fatigue",
      "tired",
      "weakness",
      "faiblesse",
    ])
  ) {
    return {
      icon,
      tone: "watch",
      severity: fr
        ? "Souvent léger, mais important à noter si cela gêne vos activités ou votre sécurité."
        : "Often mild, but important to track if it affects your activities or safety.",
      normal: fr
        ? "Fatigue, somnolence ou vertiges légers peuvent arriver au début ou après une prise."
        : "Tiredness, drowsiness, or mild dizziness can happen when starting or soon after a dose.",
      dangerous: fr
        ? "Malaise, chute, confusion, essoufflement, palpitations, faiblesse marquée ou symptômes qui empirent."
        : "Fainting, falls, confusion, shortness of breath, palpitations, marked weakness, or worsening symptoms.",
      callDoctor: fr
        ? "Appelez si vous vous évanouissez, ne pouvez pas conduire ou fonctionner normalement, ou si le symptôme est nouveau et important."
        : "Call if you faint, cannot drive or function normally, or the symptom is new and significant.",
    };
  }

  if (
    hasAny(s, [
      "mood",
      "humeur",
      "irritab",
      "anxiety",
      "anxiété",
      "depression",
      "dépression",
      "insomnia",
      "insomnie",
      "sleep",
    ])
  ) {
    return {
      icon,
      tone: "watch",
      severity: fr
        ? "Peut être léger ou très perturbant. Les changements hormonaux peuvent amplifier le sommeil, l'anxiété et l'humeur."
        : "Can be mild or very disruptive. Hormone shifts can affect sleep, anxiety, and mood.",
      normal: fr
        ? "Irritabilité, humeur changeante, anxiété ou sommeil perturbé peuvent arriver pendant un cycle."
        : "Irritability, mood swings, anxiety, or disrupted sleep can happen during a cycle.",
      dangerous: fr
        ? "Idées de vous faire du mal, panique intense, incapacité à dormir plusieurs nuits, ou humeur basse qui devient ingérable."
        : "Thoughts of self-harm, intense panic, inability to sleep for multiple nights, or low mood that feels unmanageable.",
      callDoctor: fr
        ? "Appelez votre clinique si cela devient difficile à gérer. Utilisez l'aide d'urgence en cas de risque pour votre sécurité."
        : "Call your clinic if this becomes hard to manage. Use urgent support if your safety is at risk.",
    };
  }

  return {
    icon,
    tone: "watch",
    severity: fr
      ? "Généralement léger à modéré, mais le contexte compte. Notez l'intensité, la fréquence et l'évolution."
      : "Usually mild to moderate, but context matters. Track intensity, frequency, and whether it is changing.",
    normal: fr
      ? "Des symptômes légers et transitoires peuvent arriver avec les médicaments ou les changements hormonaux."
      : "Mild, temporary symptoms can happen with medications or hormone shifts.",
    dangerous: fr
      ? "Symptôme sévère, soudain, qui empire, dure plus que prévu, ou s'accompagne de fièvre, essoufflement, douleur thoracique, saignement important ou réaction allergique."
      : "Severe, sudden, worsening, longer than expected, or paired with fever, shortness of breath, chest pain, heavy bleeding, or allergic symptoms.",
    callDoctor: fr
      ? "Appelez votre clinique si cela vous inquiète, s'aggrave, ou ne correspond pas à ce qu'elle vous avait décrit."
      : "Call your clinic if it worries you, gets worse, or does not match what they told you to expect.",
  };
}

function DetailBlock({ label, body }: { label: string; body: string }) {
  return (
    <View style={{ marginTop: 14 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          fontWeight: "700",
          color: EMBER.colors.textFaint,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          marginBottom: 5,
        }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          color: EMBER.colors.textMid,
          lineHeight: 21,
        }}
      >
        {body}
      </Text>
    </View>
  );
}

function SymptomDetailModal({
  effect,
  loggedSymptom,
  note,
  onChangeNote,
  onClose,
  onSave,
  isSaving,
}: {
  effect: string | null;
  loggedSymptom?: Symptom;
  note: string;
  onChangeNote: (note: string) => void;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const t = useTranslations();
  const { locale } = useLocale();
  if (!effect) return null;

  const detail = detailText(effect, locale);
  const isLogged = !!loggedSymptom;
  const borderColor =
    detail.tone === "urgent"
      ? withAlpha(semanticColors.status.danger, 0.22)
      : detail.tone === "call"
        ? withAlpha(semanticColors.status.warning, 0.22)
        : semanticColors.border.subtle;

  return (
    <Dialog
      visible
      onClose={onClose}
      closeButton="minimal"
      closeAccessibilityLabel={t("learn.article_actions.close_accessibility")}
      closeAction="close_symptom_detail"
      // An urgent or call-your-clinic symptom keeps its tinted edge.
      surfaceStyle={{ borderWidth: 1, borderColor }}
    >
      <ScrollView showsVerticalScrollIndicator={false}>
        <DialogHeader
          leading={
            <Text style={{ fontSize: 22, lineHeight: 25 }}>{detail.icon}</Text>
          }
          title={effect}
        />

        <DetailBlock
          label={t("medication_factsheet.symptom_detail_severity")}
          body={detail.severity}
        />
        <DetailBlock
          label={t("medication_factsheet.symptom_detail_normal")}
          body={detail.normal}
        />
        <DetailBlock
          label={t("medication_factsheet.symptom_detail_dangerous")}
          body={detail.dangerous}
        />
        <DetailBlock
          label={t("medication_factsheet.symptom_detail_call_doctor")}
          body={detail.callDoctor}
        />

        <TextField
          notes
          label={t("medication_factsheet.symptom_detail_notes_label")}
          value={note}
          onChangeText={onChangeNote}
          placeholder={t(
            "medication_factsheet.symptom_detail_notes_placeholder",
          )}
          style={{ marginTop: 18 }}
        />

        {isLogged ? (
          <SecondaryButton
            label={t("medication_factsheet.symptom_detail_update_log")}
            action="update_symptom_log"
            onPress={onSave}
            disabled={isSaving}
            style={{ marginTop: 14 }}
          />
        ) : (
          <PrimaryButton
            label={t("medication_factsheet.symptom_detail_log_button")}
            action="log_symptom"
            onPress={onSave}
            disabled={isSaving}
            style={{ marginTop: 14 }}
          />
        )}
      </ScrollView>
    </Dialog>
  );
}

export function SideEffectTags({
  sideEffects,
  symptoms,
}: {
  sideEffects?: string[];
  symptoms?: string[];
}) {
  const symptomItems = symptoms ?? sideEffects ?? [];
  const today = getTodayString();
  const { data: todaysSymptoms } = useSymptomLogs({
    startDate: today,
    endDate: today,
  });
  const logSymptom = useLogSymptom();
  const deleteSymptom = useDeleteSymptomLog();
  const [selectedEffect, setSelectedEffect] = useState<string | null>(null);
  const [note, setNote] = useState("");

  const loggedToday = useMemo(() => {
    const map = new Map<string, Symptom>();
    for (const s of todaysSymptoms ?? []) {
      map.set(normalize(s.symptom), s);
    }
    return map;
  }, [todaysSymptoms]);

  const selectedLoggedSymptom = selectedEffect
    ? loggedToday.get(normalize(selectedEffect))
    : undefined;

  useEffect(() => {
    if (!selectedEffect) {
      setNote("");
      return;
    }
    setNote(selectedLoggedSymptom?.notes ?? "");
  }, [selectedEffect, selectedLoggedSymptom?.notes]);

  const toggleLogged = (effect: string) => {
    const logged = loggedToday.get(normalize(effect));
    if (logged) {
      deleteSymptom.mutate(logged.id);
    } else {
      logSymptom.mutate({
        symptom: effect,
        dateAndTime: new Date().toISOString(),
      });
    }
  };

  const saveSelected = async () => {
    if (!selectedEffect) return;
    const trimmedNote = note.trim();
    const logged = loggedToday.get(normalize(selectedEffect));
    if (logged) {
      if ((logged.notes ?? "").trim() === trimmedNote) {
        setSelectedEffect(null);
        return;
      }
      await deleteSymptom.mutateAsync(logged.id);
    }
    await logSymptom.mutateAsync({
      symptom: selectedEffect,
      dateAndTime: new Date().toISOString(),
      notes: trimmedNote || null,
    });
    setSelectedEffect(null);
  };

  return (
    <>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {symptomItems.map((effect, i) => {
          const isLogged = loggedToday.has(normalize(effect));
          const toggleLabel = `${isLogged ? "Remove log for" : "Log"} ${effect}`;
          return (
            <MoonFilterChip
              key={`${effect}-${i}`}
              label={effect}
              accessibilityLabel={`${effect} details`}
              selected={isLogged}
              onPress={() => setSelectedEffect(effect)}
              action="open_side_effect_detail"
              // The chips sit on a light glass card, where the glass tone's
              // white-on-white fades out; the outline tone reads and taps.
              tone="outline"
              showCheck={false}
              leading={
                <Text style={{ fontSize: 14, lineHeight: 18 }}>
                  {getSymptomIcon(effect)}
                </Text>
              }
              trailing={
                // Icon-only toggle; its accessibility label is the copy.
                <IconButton
                  accessibilityLabel={toggleLabel}
                  action={isLogged ? "unlog_side_effect" : "log_side_effect"}
                  onPress={() => toggleLogged(effect)}
                  size={22}
                  hitSlop={6}
                >
                  {/* The toggle takes the chip label's colour. */}
                  {isLogged ? (
                    <Check
                      size={13}
                      color={colors.deepBrown}
                      strokeWidth={2.5}
                    />
                  ) : (
                    <Plus size={14} color={colors.text} strokeWidth={2.2} />
                  )}
                </IconButton>
              }
            />
          );
        })}
      </View>

      <SymptomDetailModal
        effect={selectedEffect}
        loggedSymptom={selectedLoggedSymptom}
        note={note}
        onChangeNote={setNote}
        onClose={() => setSelectedEffect(null)}
        onSave={saveSelected}
        isSaving={logSymptom.isPending || deleteSymptom.isPending}
      />
    </>
  );
}
